import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../supabase';
import { useWhitelistRole } from '../useWhitelistRole';
import { PhoneField, formatPhoneDisplay } from './PhoneField';
import { BankAccountField, formatBankAccountDisplay, formatCciDisplay } from './BankAccountField';
import useBreakpoint from '../hooks/useBreakpoint';
import { sheet, toastMobile, pageMobile, tap, dialog } from '../mobileStyles';
import BottomSheet from './BottomSheet';

const CARGO_OPTIONS = [
  'Arquitecta Principal',
  'Diseñadora',
  'Asistente de diseño',
  'Expediente técnico',
  'Cotizaciones',
  'Proveedor',
  'Contratista',
  'Equipo másquemuros',
  'Otro',
];

const COLOR_PALETTE = [
  '#6B1C2A', '#B83B3B', '#C97B32', '#C9A96E',
  '#4A7C59', '#2A5C3E', '#1C3E6B', '#6B2A5C',
  '#3E2A1A', '#7A8B8B', '#8B6914', '#4A1219',
];
const PHOTO_BUCKET = 'equipo-fotos';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function CameraIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h2l1.5-2h5L14 7h2a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V9a2 2 0 012-2z" />
      <circle cx="10" cy="12" r="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h14" />
      <path d="M8 6V4a1 1 0 011-1h2a1 1 0 011 1v2" />
      <path d="M5 6l1 11a2 2 0 002 2h4a2 2 0 002-2l1-11" />
      <line x1="9" y1="10" x2="9" y2="15" />
      <line x1="11" y1="10" x2="11" y2="15" />
    </svg>
  );
}

const EMPTY_FORM = { nombre: '', cargo: 'Diseñadora', email: '', whatsapp: '', color: '#6B1C2A', estado: 'Activo' };
const EMPTY_PROFILE_DRAFT = {
  nombre: '', cargo: '', email: '', whatsapp: '', estado: 'Activo', color: '#6B1C2A',
  cuenta_bancaria: '', cuenta_cci: '',
  contacto_emergencia_nombre: '', contacto_emergencia_telefono: '',
};
const EMPTY_PROV = {
  nombre: '', especialidad: '', telefono: '', email: '', notas: '',
  cuenta_bancaria: '', cuenta_cci: '',
  contacto_emergencia_nombre: '', contacto_emergencia_telefono: '',
  activo: true,
};

export default function Equipo({ onNavigate } = {}) {
  const { isAdmin, email: currentEmail } = useWhitelistRole();
  const { isMobile, isTablet, isCompact } = useBreakpoint();
  // Enlarged tap targets / always-visible hover actions on phones and tablets (< 1024px).
  const bigTap = isCompact;

  // A card is editable by its owner (email match) or by any admin. Sensitive
  // fields (cuenta bancaria, contacto de emergencia) are also only shown to
  // the same audience — so a member can see & update their own data but
  // never another member's.
  const canEditMember = (m) => isAdmin || (!!m?.email && !!currentEmail && m.email === currentEmail);
  const canViewMemberSensitive = canEditMember;

  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [selectedMember, setSelectedMember] = useState(null);
  const [profileDraft, setProfileDraft] = useState(EMPTY_PROFILE_DRAFT);
  const [profileSaving, setProfileSaving] = useState(false);

  const [uploadingId, setUploadingId] = useState(null);
  const cardFileInputs = useRef({});
  const profileFileInput = useRef(null);

  const [confirmDelete, setConfirmDelete] = useState(null); // { kind, item, title, message }
  const [deletingConfirm, setDeletingConfirm] = useState(false);

  // Shared roles label set for cargo + especialidad inputs
  const [roles, setRoles] = useState([]);
  const [especialidadOptions, setEspecialidadOptions] = useState([]);

  // Equipo de Obra (proveedores)
  const [proveedores, setProveedores] = useState([]);
  const [loadingProv, setLoadingProv] = useState(false);
  const [provModal, setProvModal] = useState(null); // { draft, editingId? } | null
  const [provSaving, setProvSaving] = useState(false);
  const [deletingProvId, setDeletingProvId] = useState(null);
  const [hoveredProvId, setHoveredProvId] = useState(null);
  const [provSearch, setProvSearch] = useState('');
  const [provEspecialidadFilters, setProvEspecialidadFilters] = useState(() => new Set());
  const [provEspecialidadOpen, setProvEspecialidadOpen] = useState(false);
  const provEspecialidadRef = useRef(null);
  const closeEspSheet = useCallback(() => setProvEspecialidadOpen(false), []);
  const provFileInputs = useRef({});

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('equipo').select('*').order('created_at', { ascending: true });
    if (error) { setTableMissing(true); setLoading(false); return; }
    setTableMissing(false);
    setMembers(data || []);
    setLoading(false);
  }, []);

  const fetchTasksAndProjects = useCallback(async () => {
    const [tRes, pRes] = await Promise.all([
      supabase.from('tareas').select('*'),
      supabase.from('proyectos').select('id, nombre, arquitecta_id, arquitecta_nombre, estado'),
    ]);
    if (tRes.data) setTasks(tRes.data);
    if (pRes.data) setProjects(pRes.data);
  }, []);

  useEffect(() => { fetchMembers(); fetchTasksAndProjects(); }, [fetchMembers, fetchTasksAndProjects]);

  const fetchProveedores = useCallback(async () => {
    setLoadingProv(true);
    const { data, error } = await supabase.from('proveedores').select('*').order('nombre');
    if (error) { showToast('Error cargando proveedores: ' + error.message, 'error'); setLoadingProv(false); return; }
    setProveedores(data || []);
    setLoadingProv(false);
  }, []);

  useEffect(() => { if (isAdmin) fetchProveedores(); }, [isAdmin, fetchProveedores]);

  const fetchRoles = useCallback(async () => {
    const { data, error } = await supabase.from('roles').select('nombre').order('nombre');
    if (error) {
      // Table might not exist yet if the migration hasn't run — stay silent
      // and fall back to CARGO_OPTIONS in the inputs below.
      console.warn('[equipo] roles fetch failed (falling back to CARGO_OPTIONS):', error.message);
      return;
    }
    setRoles(data || []);
  }, []);
  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const fetchEspecialidades = useCallback(async () => {
    const { data, error } = await supabase.from('especialidades').select('nombre').order('nombre');
    if (error) {
      console.warn('[equipo] especialidades fetch failed:', error.message);
      setEspecialidadOptions([]);
      return;
    }
    setEspecialidadOptions((data || []).map(e => e.nombre));
  }, []);
  useEffect(() => { fetchEspecialidades(); }, [fetchEspecialidades]);

  // Unified list powering the datalist: DB-first, hardcoded CARGO_OPTIONS
  // as a fallback so the inputs still have useful suggestions even before
  // supabase-roles-migration.sql has been run.
  const roleSuggestions = useMemo(() => {
    const names = roles.length ? roles.map(r => r.nombre) : CARGO_OPTIONS;
    return Array.from(new Set(names)).sort();
  }, [roles]);

  const uniqueProvEspecialidades = useMemo(() => {
    const names = proveedores
      .map(p => (p.especialidad || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [proveedores]);

  const filteredProveedores = useMemo(() => {
    const q = provSearch.trim().toLowerCase();
    return proveedores.filter(p => {
      if (provEspecialidadFilters.size > 0) {
        if (!p.especialidad || !provEspecialidadFilters.has(p.especialidad)) return false;
      }
      if (!q) return true;
      const haystack = `${p.nombre || ''} ${p.especialidad || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [proveedores, provSearch, provEspecialidadFilters]);

  function toggleEspecialidadFilter(esp) {
    setProvEspecialidadFilters(prev => {
      const next = new Set(prev);
      if (next.has(esp)) next.delete(esp); else next.add(esp);
      return next;
    });
  }
  function clearEspecialidadFilters() {
    setProvEspecialidadFilters(new Set());
  }

  useEffect(() => {
    if (!provEspecialidadOpen) return;
    function onDoc(e) {
      if (provEspecialidadRef.current && !provEspecialidadRef.current.contains(e.target)) {
        setProvEspecialidadOpen(false);
      }
    }
    function onKey(e) { if (e.key === 'Escape') setProvEspecialidadOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [provEspecialidadOpen]);

  function openNewProveedor() { fetchRoles(); fetchEspecialidades(); setProvModal({ draft: { ...EMPTY_PROV } }); }
  function openEditProveedor(p) {
    fetchRoles();
    fetchEspecialidades();
    setProvModal({
      editingId: p.id,
      draft: {
        nombre: p.nombre || '',
        especialidad: p.especialidad || '',
        telefono: p.telefono || '',
        email: p.email || '',
        notas: p.notas || '',
        cuenta_bancaria: p.cuenta_bancaria || '',
        cuenta_cci: p.cuenta_cci || '',
        contacto_emergencia_nombre: p.contacto_emergencia_nombre || '',
        contacto_emergencia_telefono: p.contacto_emergencia_telefono || '',
        activo: p.activo !== false,
      },
    });
  }
  function closeProvModal() { setProvModal(null); }

  async function saveProveedor(e) {
    e.preventDefault();
    if (!provModal || provSaving) return;
    const d = provModal.draft;
    if (!d.nombre.trim()) return;
    setProvSaving(true);
    const payload = {
      nombre: d.nombre.trim(),
      especialidad: d.especialidad || null,
      telefono: d.telefono || null,
      email: d.email || null,
      notas: d.notas || null,
      cuenta_bancaria: d.cuenta_bancaria || null,
      cuenta_cci: d.cuenta_cci || null,
      contacto_emergencia_nombre: d.contacto_emergencia_nombre || null,
      contacto_emergencia_telefono: d.contacto_emergencia_telefono || null,
      activo: d.activo !== false,
    };
    let resp;
    if (provModal.editingId) {
      resp = await supabase.from('proveedores').update(payload).eq('id', provModal.editingId).select();
    } else {
      resp = await supabase.from('proveedores').insert([payload]).select();
    }
    setProvSaving(false);
    if (resp.error) {
      if (/column .* does not exist/i.test(resp.error.message || '')) {
        showToast('Faltan columnas nuevas — ejecuta supabase-proveedores-extra-migration.sql y supabase-cuenta-cci-migration.sql', 'error');
      } else {
        showToast('Error: ' + resp.error.message, 'error');
      }
      return;
    }
    if (!resp.data || resp.data.length === 0) {
      showToast('No se guardó (posible bloqueo RLS en proveedores)', 'error');
      return;
    }
    await fetchProveedores();
    setProvModal(null);
    showToast(provModal.editingId ? 'Proveedor actualizado' : 'Proveedor creado');
  }

  function requestDeleteMember(m) {
    if (!m) return;
    if (m.email && currentEmail && m.email.toLowerCase() === currentEmail.toLowerCase()) {
      showToast('No puedes eliminarte a ti mismo', 'error');
      return;
    }
    setConfirmDelete({
      kind: 'member',
      item: m,
      title: 'Eliminar miembro',
      message: `¿Eliminar a ${m.nombre} del equipo? Esta acción también revocará su acceso al sistema.`,
    });
  }

  function requestDeleteProv(p) {
    if (!p) return;
    setConfirmDelete({
      kind: 'prov',
      item: p,
      title: 'Eliminar proveedor',
      message: `¿Eliminar al proveedor "${p.nombre}"? Esta acción no se puede deshacer.`,
    });
  }

  function closeConfirmDelete() {
    if (deletingConfirm) return;
    setConfirmDelete(null);
  }

  async function runConfirmDelete() {
    if (!confirmDelete || deletingConfirm) return;
    setDeletingConfirm(true);
    try {
      if (confirmDelete.kind === 'member') {
        const m = confirmDelete.item;
        const { data, error } = await supabase.from('equipo').delete().eq('id', m.id).select();
        if (error) { showToast('Error: ' + error.message, 'error'); return; }
        if (!data || data.length === 0) { showToast('No se eliminó (posible bloqueo RLS en equipo)', 'error'); return; }
        if (m.email) {
          const { error: wlErr } = await supabase.from('whitelist').delete().eq('email', m.email);
          if (wlErr) console.warn('[whitelist] delete failed:', wlErr.message);
        }
        setMembers(prev => prev.filter(x => x.id !== m.id));
        setConfirmDelete(null);
        showToast('Miembro eliminado y acceso revocado');
      } else if (confirmDelete.kind === 'prov') {
        const p = confirmDelete.item;
        setDeletingProvId(p.id);
        const { data, error } = await supabase.from('proveedores').delete().eq('id', p.id).select();
        setDeletingProvId(null);
        if (error) { showToast('Error: ' + error.message, 'error'); return; }
        if (!data || data.length === 0) { showToast('No se eliminó (posible bloqueo RLS en proveedores)', 'error'); return; }
        setProveedores(prev => prev.filter(x => x.id !== p.id));
        setConfirmDelete(null);
        showToast('Proveedor eliminado');
      }
    } finally {
      setDeletingConfirm(false);
    }
  }

  async function uploadProveedorPhoto(prov, file) {
    if (!prov || !file) return;
    if (!file.type.startsWith('image/')) {
      showToast('El archivo debe ser una imagen', 'error');
      return;
    }
    setUploadingId(prov.id);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `proveedores/${prov.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, {
      cacheControl: '3600', upsert: false, contentType: file.type,
    });
    if (upErr) {
      setUploadingId(null);
      showToast('Error subiendo foto: ' + upErr.message, 'error');
      return;
    }
    const { data: pub } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path);
    const foto_url = pub?.publicUrl || null;
    const { data, error } = await supabase.from('proveedores').update({ foto_url }).eq('id', prov.id).select();
    setUploadingId(null);
    if (error) {
      if (/column .* does not exist/i.test(error.message || '')) {
        showToast('Falta columna foto_url — ejecuta supabase-proveedores-foto-migration.sql', 'error');
      } else {
        showToast('Error guardando foto: ' + error.message, 'error');
      }
      return;
    }
    if (!data || data.length === 0) {
      showToast('No se actualizó (posible bloqueo RLS en proveedores)', 'error');
      return;
    }
    await fetchProveedores();
    showToast('Foto actualizada');
  }

  async function deleteProveedorPhoto(prov) {
    if (!prov || !prov.foto_url) return;
    if (!window.confirm('¿Eliminar la foto del proveedor?')) return;
    setUploadingId(prov.id);
    const marker = `/${PHOTO_BUCKET}/`;
    const idx = prov.foto_url.indexOf(marker);
    if (idx >= 0) {
      const path = prov.foto_url.slice(idx + marker.length).split('?')[0];
      const { error: remErr } = await supabase.storage.from(PHOTO_BUCKET).remove([path]);
      if (remErr) console.warn('[proveedores] storage.remove failed:', remErr);
    }
    const { data, error } = await supabase.from('proveedores').update({ foto_url: null }).eq('id', prov.id).select();
    setUploadingId(null);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      showToast('No se actualizó (posible bloqueo RLS en proveedores)', 'error');
      return;
    }
    await fetchProveedores();
    showToast('Foto eliminada');
  }

  function triggerProvUpload(provId) {
    const input = provFileInputs.current[provId];
    if (input) input.click();
  }
  function onProvFile(prov, e) {
    const file = e.target.files?.[0];
    if (file) uploadProveedorPhoto(prov, file);
    e.target.value = '';
  }

  function taskCountFor(memberName) {
    if (!memberName) return 0;
    return tasks.filter(t => t.asignado === memberName || t.asignado?.startsWith(memberName.split(' ')[0])).length;
  }
  function tasksForMember(memberName) {
    if (!memberName) return [];
    return tasks.filter(t => t.asignado === memberName || t.asignado?.startsWith(memberName.split(' ')[0]));
  }
  function projectsForMember(member) {
    if (!member) return [];
    return projects.filter(p =>
      (member.id && p.arquitecta_id === member.id) ||
      (!p.arquitecta_id && member.nombre && p.arquitecta_nombre === member.nombre)
    );
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const { error } = await supabase.from('equipo').insert([form]);
    if (error) {
      setSaving(false);
      showToast('Error: ' + error.message, 'error');
      return;
    }
    // Sync to whitelist so the new member can actually log in.
    // Skipped silently when no email was provided (nothing to gate on).
    const didWhitelist = await ensureWhitelistEntry({ email: form.email, nombre: form.nombre });
    setSaving(false);
    setShowModal(false);
    setForm(EMPTY_FORM);
    fetchMembers();
    showToast(didWhitelist ? 'Miembro agregado y acceso configurado' : 'Miembro agregado');
  }

  // Insert a miembro-level whitelist row for the given email if it doesn't
  // exist yet. No-ops when email is missing or the row is already there.
  // Returns true when a new row was created (or already existed, i.e. access
  // is effectively configured), false when there was no email to work with.
  async function ensureWhitelistEntry({ email, nombre }) {
    const trimmed = (email || '').trim();
    if (!trimmed) return false;
    const { data: existing, error: selErr } = await supabase
      .from('whitelist')
      .select('id')
      .eq('email', trimmed)
      .maybeSingle();
    if (selErr) { console.warn('[whitelist] select failed:', selErr.message); return false; }
    if (existing) return true; // already configured — treated as success
    const { error: insErr } = await supabase
      .from('whitelist')
      .insert([{ email: trimmed, nombre: nombre || null, rol: 'miembro', activo: true }]);
    if (insErr) { console.warn('[whitelist] insert failed:', insErr.message); return false; }
    return true;
  }

  function openAdd() { fetchRoles(); setForm(EMPTY_FORM); setShowModal(true); }

  async function uploadPhoto(member, file) {
    if (!member || !file) return;
    if (!file.type.startsWith('image/')) {
      showToast('El archivo debe ser una imagen', 'error');
      return;
    }
    setUploadingId(member.id);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${member.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, {
      cacheControl: '3600', upsert: false, contentType: file.type,
    });
    if (upErr) {
      setUploadingId(null);
      showToast('Error subiendo foto: ' + upErr.message, 'error');
      return;
    }
    const { data: pub } = supabase.storage.from(PHOTO_BUCKET).getPublicUrl(path);
    const foto_url = pub?.publicUrl || null;
    const { data, error } = await supabase.from('equipo').update({ foto_url }).eq('id', member.id).select();
    setUploadingId(null);
    if (error) { showToast('Error guardando foto: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      showToast('No se actualizó (posible bloqueo RLS en equipo)', 'error');
      return;
    }
    await fetchMembers();
    setSelectedMember(prev => prev && prev.id === member.id ? { ...prev, foto_url } : prev);
    showToast('Foto actualizada');
  }

  function triggerCardUpload(memberId) {
    const input = cardFileInputs.current[memberId];
    if (input) input.click();
  }
  function onCardFile(member, e) {
    const file = e.target.files?.[0];
    if (file) uploadPhoto(member, file);
    e.target.value = '';
  }
  function onProfileFile(e) {
    const file = e.target.files?.[0];
    if (file && selectedMember) uploadPhoto(selectedMember, file);
    e.target.value = '';
  }

  async function deletePhoto() {
    if (!selectedMember || !selectedMember.foto_url) return;
    setUploadingId(selectedMember.id);
    // Extract object path from the public URL — format:
    //   https://<proj>.supabase.co/storage/v1/object/public/equipo-fotos/<path>
    const marker = `/${PHOTO_BUCKET}/`;
    const idx = selectedMember.foto_url.indexOf(marker);
    if (idx >= 0) {
      const path = selectedMember.foto_url.slice(idx + marker.length).split('?')[0];
      const { error: remErr } = await supabase.storage.from(PHOTO_BUCKET).remove([path]);
      if (remErr) console.warn('[equipo] storage.remove failed:', remErr);
    }
    const { data, error } = await supabase
      .from('equipo')
      .update({ foto_url: null })
      .eq('id', selectedMember.id)
      .select();
    setUploadingId(null);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      showToast('No se actualizó (posible bloqueo RLS en equipo)', 'error');
      return;
    }
    await fetchMembers();
    setSelectedMember(prev => prev ? { ...prev, foto_url: null } : prev);
    showToast('Foto eliminada');
  }

  function openProfile(m) {
    setSelectedMember(m);
    // Pull fresh tareas/proyectos so a task deleted in the Gantt doesn't
    // linger in this member's "Tareas asignadas" list.
    fetchTasksAndProjects();
    if (canEditMember(m)) {
      fetchRoles();
      setProfileDraft({
        nombre: m.nombre || '',
        cargo: m.cargo || '',
        email: m.email || '',
        whatsapp: m.whatsapp || '',
        estado: m.estado || 'Activo',
        color: m.color || '#6B1C2A',
        cuenta_bancaria: m.cuenta_bancaria || '',
        cuenta_cci: m.cuenta_cci || '',
        contacto_emergencia_nombre: m.contacto_emergencia_nombre || '',
        contacto_emergencia_telefono: m.contacto_emergencia_telefono || '',
      });
    } else {
      setProfileDraft(EMPTY_PROFILE_DRAFT);
    }
  }
  function closeProfile() {
    setSelectedMember(null);
  }
  function handleProfileChange(e) {
    const { name, value } = e.target;
    setProfileDraft(prev => ({ ...prev, [name]: value }));
  }
  async function saveProfile() {
    if (!selectedMember) return;
    // Defensive guard: a non-admin must only be able to save their own card.
    // The UI already hides the Editar button in that case, but this ensures
    // a crafted submit can't mutate someone else's row either.
    if (!canEditMember(selectedMember)) {
      showToast('No tienes permiso para editar este perfil', 'error');
      return;
    }
    setProfileSaving(true);
    const payload = {
      nombre: profileDraft.nombre,
      cargo: profileDraft.cargo,
      email: profileDraft.email || null,
      whatsapp: profileDraft.whatsapp || null,
      estado: profileDraft.estado,
      color: profileDraft.color || '#6B1C2A',
      // Sensitive fields — admins can touch any row, members only their own.
      // canEditMember already checked that above, so include them here.
      cuenta_bancaria: profileDraft.cuenta_bancaria || null,
      cuenta_cci: profileDraft.cuenta_cci || null,
      contacto_emergencia_nombre: profileDraft.contacto_emergencia_nombre || null,
      contacto_emergencia_telefono: profileDraft.contacto_emergencia_telefono || null,
    };
    const oldEmail = (selectedMember.email || '').trim();
    const newEmail = (payload.email || '').trim();
    const { data, error } = await supabase.from('equipo').update(payload).eq('id', selectedMember.id).select();
    if (error) { setProfileSaving(false); showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) {
      setProfileSaving(false);
      showToast('No se actualizó (posible bloqueo RLS en equipo)', 'error');
      return;
    }
    // Keep whitelist in sync with the edited email.
    if (newEmail && oldEmail && newEmail !== oldEmail) {
      const { data: updated } = await supabase
        .from('whitelist')
        .update({ email: newEmail, nombre: payload.nombre || null })
        .eq('email', oldEmail)
        .select();
      // If no row matched the old email, create one under the new email so
      // access still gets configured.
      if (!updated || updated.length === 0) {
        await ensureWhitelistEntry({ email: newEmail, nombre: payload.nombre });
      }
    } else if (newEmail) {
      // Email unchanged (or was blank before) — make sure a row exists.
      await ensureWhitelistEntry({ email: newEmail, nombre: payload.nombre });
    }
    setProfileSaving(false);
    await fetchMembers();
    setSelectedMember(null);
    showToast('Perfil actualizado');
  }

  if (tableMissing) {
    return (
      <div style={isMobile ? { ...s.container, ...pageMobile } : s.container}>
        <header style={s.header}>
          <div>
            <h2 style={s.title}>Equipo másquemuros</h2>
            <p style={s.subtitle}>Configurar miembros del equipo</p>
          </div>
        </header>
        <div style={{ background:'#fff', borderRadius:14, border:'1px solid var(--border)', padding:'40px 30px', textAlign:'center' }}>
          <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:'var(--wine)', marginBottom:10 }}>Tabla no encontrada</h3>
          <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>
            Ejecuta <strong>supabase-equipo-migration.sql</strong> en el SQL Editor de Supabase para habilitar esta sección.
          </p>
          <button style={s.primaryBtn} onClick={fetchMembers}>Reintentar</button>
        </div>
      </div>
    );
  }

  const projectsById = {};
  for (const p of projects) projectsById[p.id] = p.nombre;

  return (
    <div style={isMobile ? { ...s.container, ...pageMobile } : s.container}>
      <header style={s.header}>
        <div>
          <h2 style={s.title}>Equipo másquemuros</h2>
          <p style={s.subtitle}>Diseño y obra del estudio</p>
        </div>
      </header>


      {/* ── SECCIÓN: EQUIPO DE DISEÑO ── */}
      <div style={isMobile ? { ...s.sectionRow, ...s.sectionRowMobile } : s.sectionRow}>
        <div>
          <h3 style={s.sectionRowTitle}>Equipo de Diseño</h3>
          <p style={s.sectionRowSub}>{members.length} {members.length === 1 ? 'miembro' : 'miembros'} registrado{members.length === 1 ? '' : 's'}</p>
        </div>
        <button style={isMobile ? { ...s.primaryBtn, ...tap } : s.primaryBtn} onClick={openAdd}>+ Agregar miembro</button>
      </div>

      {loading ? (
        <div style={isMobile ? { ...s.loadingMsg, ...s.loadingMsgMobile } : s.loadingMsg}>Cargando equipo...</div>
      ) : members.length === 0 ? (
        <div style={isMobile ? { ...s.loadingMsg, ...s.loadingMsgMobile } : s.loadingMsg}>No hay miembros aún. Agrega el primero.</div>
      ) : (
        <div style={isMobile ? { ...s.grid, ...s.gridMobile } : s.grid}>
          {members.map(m => {
            const count = taskCountFor(m.nombre);
            const projCount = projectsForMember(m).length;
            const uploading = uploadingId === m.id;
            return (
              <div key={m.id} style={s.card} onClick={() => openProfile(m)}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
              >
                <div style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
                  <div style={{ position:'relative', flexShrink:0 }}>
                    <Avatar member={m} size={48} />
                    {canEditMember(m) && (
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); triggerCardUpload(m.id); }}
                        style={bigTap ? { ...s.cameraOverlay, ...s.cameraOverlayTouch } : s.cameraOverlay}
                        disabled={uploading}
                        title={uploading ? 'Subiendo...' : 'Subir foto'}
                        aria-label="Subir foto"
                      >
                        {uploading ? '…' : <CameraIcon />}
                      </button>
                    )}
                    <input
                      ref={el => { cardFileInputs.current[m.id] = el; }}
                      type="file"
                      accept="image/*"
                      style={{ display:'none' }}
                      onChange={(e) => onCardFile(m, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                      <h4 style={s.memberName}>{m.nombre}</h4>
                      <span style={{ ...s.estadoBadge, background: m.estado === 'Activo' ? 'rgba(74,124,89,0.1)' : 'rgba(107,107,107,0.1)', color: m.estado === 'Activo' ? '#4A7C59' : '#6B6B6B' }}>
                        {m.estado || 'Activo'}
                      </span>
                    </div>
                    <p style={isMobile ? { ...s.memberRole, ...s.memberRoleMobile } : s.memberRole}>{m.cargo || '—'}</p>
                  </div>
                </div>
                <div style={s.metaRow}>
                  {m.email && <div style={s.metaItem}><span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>Email</span><span style={isMobile ? { ...s.metaValue, ...s.metaValueMobile } : s.metaValue}>{m.email}</span></div>}
                  {m.whatsapp && <div style={s.metaItem}><span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>WhatsApp</span><span style={isMobile ? { ...s.metaValue, ...s.metaValueMobile } : s.metaValue}>{formatPhoneDisplay(m.whatsapp)}</span></div>}
                </div>
                <div style={s.cardFooter}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={s.taskCount}>
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="12" height="11" rx="1.5"/><line x1="5" y1="7" x2="11" y2="7"/><line x1="5" y1="10" x2="9" y2="10"/>
                      </svg>
                      {count} tarea{count === 1 ? '' : 's'}
                    </span>
                    <span style={s.taskCount}>
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 4h12v9H2z" />
                        <path d="M2 4l3-2h6l3 2" />
                      </svg>
                      {projCount} proyecto{projCount === 1 ? '' : 's'}
                    </span>
                  </span>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    {!canEditMember(m) && (
                      <span style={{ fontSize:11, color:'var(--text-light)' }}>Ver perfil →</span>
                    )}
                    {isAdmin && !(m.email && currentEmail && m.email.toLowerCase() === currentEmail.toLowerCase()) && (
                      <button
                        type="button"
                        data-no-open
                        onClick={(ev) => { ev.stopPropagation(); requestDeleteMember(m); }}
                        style={bigTap ? { ...s.trashBtn, ...tap } : s.trashBtn}
                        title="Eliminar miembro"
                        aria-label={`Eliminar ${m.nombre}`}
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SECCIÓN: EQUIPO DE OBRA (proveedores) — admin only ── */}
      {isAdmin && (
        <>
          <div style={isMobile ? { ...s.sectionRow, marginTop: 36, ...s.sectionRowMobile } : { ...s.sectionRow, marginTop: 36 }}>
            <div>
              <h3 style={s.sectionRowTitle}>Equipo de Obra</h3>
              <p style={s.sectionRowSub}>
                {filteredProveedores.length} {filteredProveedores.length === 1 ? 'proveedor' : 'proveedores'}
                {(provSearch.trim() || provEspecialidadFilters.size > 0)
                  ? ` de ${proveedores.length}`
                  : ` registrado${proveedores.length === 1 ? '' : 's'}`}
              </p>
            </div>
            <button style={isMobile ? { ...s.primaryBtn, ...tap } : s.primaryBtn} onClick={openNewProveedor}>+ Agregar proveedor</button>
          </div>

          {proveedores.length > 0 && (
            <div style={isMobile ? { ...s.provFilterBar, ...s.provFilterBarMobile } : s.provFilterBar}>
              <div style={isMobile ? { ...s.provSearchWrap, ...s.provSearchWrapMobile } : s.provSearchWrap}>
                <span style={s.provSearchIcon} aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="9" r="6" />
                    <line x1="13.5" y1="13.5" x2="17" y2="17" />
                  </svg>
                </span>
                <input
                  style={s.provSearchInput}
                  type="text"
                  placeholder="Buscar proveedor..."
                  value={provSearch}
                  onChange={e => setProvSearch(e.target.value)}
                />
                {provSearch && (
                  <button
                    type="button"
                    onClick={() => setProvSearch('')}
                    style={bigTap ? { ...s.provSearchClear, ...s.provSearchClearTouch } : s.provSearchClear}
                    title="Limpiar búsqueda"
                    aria-label="Limpiar búsqueda"
                  >
                    ✕
                  </button>
                )}
              </div>

              {uniqueProvEspecialidades.length > 0 && (
                <div style={s.provEspWrap} ref={provEspecialidadRef}>
                  <button
                    type="button"
                    onClick={() => setProvEspecialidadOpen(v => !v)}
                    style={bigTap ? { ...s.provEspBtn, ...(provEspecialidadFilters.size > 0 ? s.provEspBtnActive : {}), ...tap } : { ...s.provEspBtn, ...(provEspecialidadFilters.size > 0 ? s.provEspBtnActive : {}) }}
                    aria-haspopup="listbox"
                    aria-expanded={provEspecialidadOpen}
                  >
                    Especialidad
                    {provEspecialidadFilters.size > 0 && ` (${provEspecialidadFilters.size})`}
                    <span style={s.provEspCaret} aria-hidden="true">▾</span>
                  </button>
                  {!isMobile && provEspecialidadOpen && (
                    <div style={s.provEspDropdown} role="listbox">
                      <div style={s.provEspList}>
                        {uniqueProvEspecialidades.map(esp => {
                          const active = provEspecialidadFilters.has(esp);
                          return (
                            <button
                              key={esp}
                              type="button"
                              onClick={() => toggleEspecialidadFilter(esp)}
                              style={s.provEspItem}
                              role="option"
                              aria-selected={active}
                            >
                              <span style={{ ...s.provEspCheckbox, ...(active ? s.provEspCheckboxActive : {}) }}>
                                {active && '✓'}
                              </span>
                              <span style={s.provEspItemLabel}>{esp}</span>
                            </button>
                          );
                        })}
                      </div>
                      {provEspecialidadFilters.size > 0 && (
                        <button
                          type="button"
                          onClick={clearEspecialidadFilters}
                          style={s.provEspClearBtn}
                        >
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  )}
                  {isMobile && (
                    <BottomSheet
                      open={provEspecialidadOpen}
                      onClose={closeEspSheet}
                      title="Especialidad"
                      footer={(
                        <>
                          <button
                            type="button"
                            onClick={clearEspecialidadFilters}
                            disabled={provEspecialidadFilters.size === 0}
                            style={{ ...s.cancelBtn, ...s.actionBtnMobile }}
                          >
                            Limpiar
                          </button>
                          <button type="button" onClick={closeEspSheet} style={{ ...s.primaryBtn, ...s.actionBtnMobile }}>
                            Listo
                          </button>
                        </>
                      )}
                    >
                      {uniqueProvEspecialidades.map(esp => {
                        const active = provEspecialidadFilters.has(esp);
                        return (
                          <button
                            key={esp}
                            type="button"
                            onClick={() => toggleEspecialidadFilter(esp)}
                            style={{ ...s.provEspItem, ...s.provEspItemMobile }}
                            aria-pressed={active}
                          >
                            <span style={{ ...s.provEspCheckbox, ...(active ? s.provEspCheckboxActive : {}) }}>
                              {active && '✓'}
                            </span>
                            <span style={s.provEspItemLabel}>{esp}</span>
                          </button>
                        );
                      })}
                    </BottomSheet>
                  )}
                </div>
              )}
            </div>
          )}

          {loadingProv ? (
            <div style={isMobile ? { ...s.loadingMsg, ...s.loadingMsgMobile } : s.loadingMsg}>Cargando proveedores…</div>
          ) : proveedores.length === 0 ? (
            <div style={isMobile ? { ...s.loadingMsg, ...s.loadingMsgMobile } : s.loadingMsg}>No hay proveedores todavía. Agrega el primero.</div>
          ) : filteredProveedores.length === 0 ? (
            <div style={isMobile ? { ...s.loadingMsg, ...s.loadingMsgMobile } : s.loadingMsg}>Sin resultados para los filtros aplicados.</div>
          ) : (
            <div style={isMobile ? { ...s.grid, ...s.gridMobile } : s.grid}>
              {filteredProveedores.map(p => {
                const uploading = uploadingId === p.id;
                const hovered = hoveredProvId === p.id;
                return (
                <div
                  key={p.id}
                  style={s.card}
                  onClick={(ev) => {
                    if (ev.target.closest('[data-no-open]')) return;
                    openEditProveedor(p);
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                >
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div
                      style={{ position: 'relative', flexShrink: 0 }}
                      onMouseEnter={() => setHoveredProvId(p.id)}
                      onMouseLeave={() => setHoveredProvId(null)}
                    >
                      {p.foto_url ? (
                        <div style={{
                          width: 48, height: 48, borderRadius: '50%',
                          background: `#fff url(${p.foto_url}) center/cover no-repeat`,
                          boxShadow: '0 0 0 2px rgba(62,33,24,0.15)',
                        }} />
                      ) : (
                        <div style={s.avatarMark}>{getInitials(p.nombre)}</div>
                      )}
                      {(bigTap ? true : (hovered || uploading)) && (
                        <button
                          type="button"
                          data-no-open
                          onClick={(ev) => { ev.stopPropagation(); triggerProvUpload(p.id); }}
                          style={bigTap ? { ...s.cameraOverlay, ...s.cameraOverlayTouch } : s.cameraOverlay}
                          disabled={uploading}
                          title={uploading ? 'Subiendo...' : (p.foto_url ? 'Cambiar foto' : 'Subir foto')}
                          aria-label={p.foto_url ? 'Cambiar foto' : 'Subir foto'}
                        >
                          {uploading ? '…' : <CameraIcon />}
                        </button>
                      )}
                      {(bigTap ? p.foto_url && !uploading : hovered && p.foto_url && !uploading) && (
                        <button
                          type="button"
                          data-no-open
                          onClick={(ev) => { ev.stopPropagation(); deleteProveedorPhoto(p); }}
                          style={bigTap ? { ...s.deletePhotoOverlay, ...s.deletePhotoOverlayTouch } : s.deletePhotoOverlay}
                          title="Eliminar foto"
                          aria-label="Eliminar foto"
                        >
                          ✕
                        </button>
                      )}
                      <input
                        ref={el => { provFileInputs.current[p.id] = el; }}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => onProvFile(p, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <h4 style={s.memberName}>{p.nombre}</h4>
                        <span style={{
                          ...s.estadoBadge,
                          background: p.activo !== false ? 'rgba(74,124,89,0.1)' : 'rgba(107,107,107,0.1)',
                          color: p.activo !== false ? '#4A7C59' : '#6B6B6B',
                        }}>
                          {p.activo !== false ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <p style={isMobile ? { ...s.memberRole, ...s.memberRoleMobile } : s.memberRole}>{p.especialidad || '—'}</p>
                    </div>
                  </div>

                  <div style={s.metaRow}>
                    {p.telefono && <div style={s.metaItem}><span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>Teléfono</span><span style={isMobile ? { ...s.metaValue, ...s.metaValueMobile } : s.metaValue}>{formatPhoneDisplay(p.telefono)}</span></div>}
                    {p.email && <div style={s.metaItem}><span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>Email</span><span style={isMobile ? { ...s.metaValue, ...s.metaValueMobile } : s.metaValue}>{p.email}</span></div>}
                    {p.cuenta_bancaria && (
                      <div style={s.metaItem}>
                        <span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>Cuenta bancaria <span style={s.adminTag}>Admin</span></span>
                        <span style={isMobile ? { ...s.metaValue, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', ...s.metaValueMobile } : { ...s.metaValue, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{formatBankAccountDisplay(p.cuenta_bancaria)}</span>
                      </div>
                    )}
                    {p.cuenta_cci && (
                      <div style={s.metaItem}>
                        <span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>CCI <span style={s.adminTag}>Admin</span></span>
                        <span style={isMobile ? { ...s.metaValue, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', ...s.metaValueMobile } : { ...s.metaValue, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{formatCciDisplay(p.cuenta_cci)}</span>
                      </div>
                    )}
                    {(p.contacto_emergencia_nombre || p.contacto_emergencia_telefono) && (
                      <div style={s.metaItem}>
                        <span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>Contacto emergencia <span style={s.adminTag}>Admin</span></span>
                        <span style={isMobile ? { ...s.metaValue, ...s.metaValueMobile } : s.metaValue}>
                          {p.contacto_emergencia_nombre || '—'}
                          {p.contacto_emergencia_telefono ? ` · ${formatPhoneDisplay(p.contacto_emergencia_telefono)}` : ''}
                        </span>
                      </div>
                    )}
                    {p.notas && <div style={s.metaItem}><span style={isMobile ? { ...s.metaLabel, ...s.metaLabelMobile } : s.metaLabel}>Notas</span><span style={isMobile ? { ...s.metaValue, ...s.metaValueMobile } : s.metaValue}>{p.notas}</span></div>}
                  </div>

                  <div style={{ ...s.cardFooter, justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      data-no-open
                      style={bigTap ? { ...s.trashBtn, ...tap } : s.trashBtn}
                      onClick={(ev) => { ev.stopPropagation(); requestDeleteProv(p); }}
                      disabled={deletingProvId === p.id}
                      title="Eliminar proveedor"
                      aria-label={`Eliminar ${p.nombre}`}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ADD/EDIT PROVEEDOR MODAL */}
      {provModal && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : isTablet ? { ...s.overlay, ...s.overlayTablet } : s.overlay} onClick={closeProvModal}>
          <div style={isMobile ? { ...s.modal, maxWidth: 560, ...sheet.panel } : { ...s.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...sheet.header, ...s.modalHeaderMobile } : s.modalHeader}>
              <h3 style={s.modalTitle}>{provModal.editingId ? 'Editar proveedor' : 'Nuevo proveedor'}</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeProvModal}>✕</button>
            </div>
            <form onSubmit={saveProveedor} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Nombre</span>
                  <input
                    style={s.input}
                    required
                    autoFocus
                    value={provModal.draft.nombre}
                    onChange={e => setProvModal(m => ({ ...m, draft: { ...m.draft, nombre: e.target.value } }))}
                  />
                </label>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Especialidad</span>
                  <select
                    style={s.input}
                    value={provModal.draft.especialidad || ''}
                    onChange={e => setProvModal(m => ({ ...m, draft: { ...m.draft, especialidad: e.target.value } }))}
                  >
                    <option value="">— Selecciona una especialidad —</option>
                    {especialidadOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                    {provModal.draft.especialidad && !especialidadOptions.includes(provModal.draft.especialidad) && (
                      <option value={provModal.draft.especialidad}>{provModal.draft.especialidad}</option>
                    )}
                  </select>
                </label>
              </div>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <PhoneField
                  label="Teléfono"
                  value={provModal.draft.telefono}
                  onChange={(v) => setProvModal(m => ({ ...m, draft: { ...m.draft, telefono: v } }))}
                />
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Email</span>
                  <input
                    style={s.input}
                    type="email"
                    placeholder="contacto@proveedor.com"
                    value={provModal.draft.email}
                    onChange={e => setProvModal(m => ({ ...m, draft: { ...m.draft, email: e.target.value } }))}
                  />
                </label>
              </div>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Notas</span>
                <textarea
                  style={{ ...s.input, minHeight: 70, resize: 'vertical', fontFamily: "'DM Sans',sans-serif" }}
                  value={provModal.draft.notas}
                  onChange={e => setProvModal(m => ({ ...m, draft: { ...m.draft, notas: e.target.value } }))}
                />
              </label>

              <div style={s.adminSection}>
                <span style={s.adminSectionTag}>Solo admins</span>
              </div>

              <div style={s.fieldGroup}>
                <span style={s.fieldLabel}>Cuenta bancaria</span>
                <BankAccountField
                  account={provModal.draft.cuenta_bancaria}
                  cci={provModal.draft.cuenta_cci}
                  onAccountChange={(v) => setProvModal(m => ({ ...m, draft: { ...m.draft, cuenta_bancaria: v } }))}
                  onCciChange={(v) => setProvModal(m => ({ ...m, draft: { ...m.draft, cuenta_cci: v } }))}
                />
              </div>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Contacto emergencia — nombre</span>
                  <input
                    style={s.input}
                    value={provModal.draft.contacto_emergencia_nombre}
                    onChange={e => setProvModal(m => ({ ...m, draft: { ...m.draft, contacto_emergencia_nombre: e.target.value } }))}
                  />
                </label>
                <PhoneField
                  label="Contacto emergencia — teléfono"
                  value={provModal.draft.contacto_emergencia_telefono}
                  onChange={(v) => setProvModal(m => ({ ...m, draft: { ...m.draft, contacto_emergencia_telefono: v } }))}
                />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={provModal.draft.activo !== false}
                  onChange={e => setProvModal(m => ({ ...m, draft: { ...m.draft, activo: e.target.checked } }))}
                />
                <span>Proveedor activo</span>
              </label>

              <div style={isMobile ? { ...s.modalActions, ...sheet.footer, ...s.modalActionsMobile } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...s.actionBtnMobile } : s.cancelBtn} onClick={closeProvModal}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.primaryBtn, ...s.actionBtnMobile } : s.primaryBtn} disabled={provSaving}>
                  {provSaving ? 'Guardando…' : (provModal.editingId ? 'Guardar cambios' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD MEMBER MODAL */}
      {showModal && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : isTablet ? { ...s.overlay, ...s.overlayTablet } : s.overlay} onClick={() => setShowModal(false)}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...sheet.header, ...s.modalHeaderMobile } : s.modalHeader}>
              <h3 style={s.modalTitle}>Agregar miembro</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Nombre completo</span>
                <input style={s.input} name="nombre" value={form.nombre} onChange={handleChange} required autoFocus />
              </label>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Cargo</span>
                <RoleSelect
                  value={form.cargo}
                  onChange={(v) => setForm(prev => ({ ...prev, cargo: v }))}
                  options={roleSuggestions}
                />
              </label>
              <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                <label style={s.fieldGroup}>
                  <span style={s.fieldLabel}>Email</span>
                  <input style={s.input} type="email" name="email" value={form.email} onChange={handleChange} placeholder="nombre@ejemplo.com" />
                </label>
                <PhoneField
                  label="WhatsApp"
                  value={form.whatsapp}
                  onChange={(v) => setForm(prev => ({ ...prev, whatsapp: v }))}
                />
              </div>
              <div style={s.fieldGroup}>
                <span style={s.fieldLabel}>Color de avatar</span>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {COLOR_PALETTE.map(c => (
                    <button key={c} type="button" onClick={() => setForm(prev => ({ ...prev, color: c }))}
                      style={bigTap ? { ...{ width:32, height:32, borderRadius:'50%', background:c, border: form.color === c ? '3px solid #2D2D2D' : '2px solid #fff', cursor:'pointer', boxShadow:'0 1px 4px rgba(0,0,0,0.15)' }, width:44, height:44 } : { width:32, height:32, borderRadius:'50%', background:c, border: form.color === c ? '3px solid #2D2D2D' : '2px solid #fff', cursor:'pointer', boxShadow:'0 1px 4px rgba(0,0,0,0.15)' }} />
                  ))}
                </div>
              </div>
              <div style={isMobile ? { ...s.modalActions, ...sheet.footer, ...s.modalActionsMobile } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...s.actionBtnMobile } : s.cancelBtn} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.primaryBtn, ...s.actionBtnMobile } : s.primaryBtn} disabled={saving}>{saving ? 'Guardando...' : 'Agregar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MEMBER PROFILE MODAL */}
      {selectedMember && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : isTablet ? { ...s.overlay, ...s.overlayTablet } : s.overlay} onClick={closeProfile}>
          <div style={isMobile ? { ...s.modal, maxWidth: 680, ...sheet.panel } : { ...s.modal, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
            {isMobile && (
              <div style={{ ...s.modalHeader, ...sheet.header, ...s.modalHeaderMobile }}>
                <h3 style={{ ...s.modalTitle, ...s.modalTitleMobile }}>{selectedMember.nombre}</h3>
                <button style={{ ...s.closeBtn, ...sheet.closeBtn }} onClick={closeProfile} aria-label="Cerrar">✕</button>
              </div>
            )}
            <div style={isMobile ? { ...s.profileHeader, ...s.profileHeaderMobile } : s.profileHeader}>
              <Avatar member={selectedMember} size={88} fontSize={26} />
              <div style={{ flex:1, minWidth:0 }}>
                <h3 style={s.modalTitle}>{selectedMember.nombre}</h3>
                <p style={s.modalSubtitle}>{selectedMember.cargo || '—'}</p>
                {canEditMember(selectedMember) && (
                  <div style={s.photoActions}>
                    {selectedMember.foto_url ? (
                      <>
                        <button
                          type="button"
                          style={bigTap ? { ...s.photoBtn, ...s.photoBtnTouch } : s.photoBtn}
                          onClick={() => profileFileInput.current?.click()}
                          disabled={uploadingId === selectedMember.id}
                        >
                          {uploadingId === selectedMember.id ? 'Subiendo…' : 'Cambiar foto'}
                        </button>
                        <button
                          type="button"
                          style={bigTap ? { ...s.photoBtnDanger, ...s.photoBtnTouch } : s.photoBtnDanger}
                          onClick={deletePhoto}
                          disabled={uploadingId === selectedMember.id}
                        >
                          Eliminar foto
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        style={bigTap ? { ...s.photoBtn, ...s.photoBtnTouch } : s.photoBtn}
                        onClick={() => profileFileInput.current?.click()}
                        disabled={uploadingId === selectedMember.id}
                      >
                        {uploadingId === selectedMember.id ? 'Subiendo…' : 'Subir foto'}
                      </button>
                    )}
                  </div>
                )}
                <input
                  ref={profileFileInput}
                  type="file"
                  accept="image/*"
                  style={{ display:'none' }}
                  onChange={onProfileFile}
                />
              </div>
              {!isMobile && <button style={s.closeBtn} onClick={closeProfile}>✕</button>}
            </div>

            <div style={isMobile ? { padding:'12px 16px 24px' } : { padding:'12px 28px 24px' }}>
              {!canEditMember(selectedMember) ? (
                <>
                  <div style={s.profileGrid}>
                    <ProfileField label="Email" value={selectedMember.email} />
                    <ProfileField label="WhatsApp" value={formatPhoneDisplay(selectedMember.whatsapp)} />
                    <ProfileField label="Estado" value={selectedMember.estado || 'Activo'} />
                    {canViewMemberSensitive(selectedMember) && (
                      <>
                        <ProfileField
                          label="Cuenta bancaria"
                          value={formatBankAccountDisplay(selectedMember.cuenta_bancaria)}
                          mono
                        />
                        {selectedMember.cuenta_cci && (
                          <ProfileField
                            label="CCI"
                            value={formatCciDisplay(selectedMember.cuenta_cci)}
                            mono
                          />
                        )}
                        <ProfileField
                          label="Contacto emergencia — nombre"
                          value={selectedMember.contacto_emergencia_nombre}
                        />
                        <ProfileField
                          label="Contacto emergencia — teléfono"
                          value={formatPhoneDisplay(selectedMember.contacto_emergencia_telefono)}
                        />
                      </>
                    )}
                  </div>

                  <h4 style={s.sectionHead}>Tareas asignadas</h4>
                  {(() => {
                    const myTasks = tasksForMember(selectedMember.nombre);
                    if (myTasks.length === 0) {
                      return <p style={{ fontSize:13, color:'var(--text-light)', padding:'8px 0 0' }}>Esta persona no tiene tareas asignadas.</p>;
                    }
                    return (
                      <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:320, overflowY:'auto' }}>
                        {myTasks.map(t => {
                          const canNavigate = !!(onNavigate && t.proyecto_id);
                          return (
                            <div
                              key={t.id}
                              onClick={canNavigate ? () => {
                                onNavigate('gantt', {
                                  projectId: t.proyecto_id,
                                  ganttType: t.tipo || 'obra',
                                  taskId: t.id,
                                });
                                closeProfile();
                              } : undefined}
                              onMouseEnter={canNavigate ? e => {
                                e.currentTarget.style.borderColor = 'rgba(107,28,42,0.35)';
                                e.currentTarget.style.background = '#fff';
                                const arrow = e.currentTarget.querySelector('[data-row-arrow]');
                                if (arrow) arrow.style.opacity = '1';
                              } : undefined}
                              onMouseLeave={canNavigate ? e => {
                                e.currentTarget.style.borderColor = 'var(--border)';
                                e.currentTarget.style.background = '#FDFBF8';
                                const arrow = e.currentTarget.querySelector('[data-row-arrow]');
                                if (arrow) arrow.style.opacity = '0';
                              } : undefined}
                              style={{
                                display:'flex', justifyContent:'space-between', alignItems:'center',
                                gap:12, padding:'10px 14px', background:'#FDFBF8',
                                border:'1px solid var(--border)', borderRadius:8,
                                cursor: canNavigate ? 'pointer' : 'default',
                                transition: 'border-color 0.15s, background 0.15s',
                              }}
                              role={canNavigate ? 'button' : undefined}
                              tabIndex={canNavigate ? 0 : undefined}
                            >
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={isMobile ? { ...{ fontSize:13, fontWeight:600, color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }, fontSize:15 } : { fontSize:13, fontWeight:600, color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.nombre}</div>
                                <div style={{ fontSize:11, color:'var(--text-light)', marginTop:2 }}>
                                  {projectsById[t.proyecto_id] || '—'} · {t.categoria}
                                </div>
                              </div>
                              <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:10, background: t.progreso === 100 ? '#4A7C59' : t.progreso > 0 ? '#C4922A' : '#E8E2D9', color: t.progreso > 0 ? '#fff' : '#6B6B6B', whiteSpace:'nowrap' }}>
                                {t.progreso ?? 0}%
                              </span>
                              {canNavigate && (
                                <span data-row-arrow style={{ color:'var(--wine)', fontSize:16, opacity:0, transition:'opacity 0.15s' }} aria-hidden="true">→</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div style={s.editForm}>
                  <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                    <label style={s.fieldGroup}>
                      <span style={s.fieldLabel}>Nombre</span>
                      <input style={s.input} name="nombre" value={profileDraft.nombre} onChange={handleProfileChange} />
                    </label>
                    <label style={s.fieldGroup}>
                      <span style={s.fieldLabel}>Cargo</span>
                      <RoleSelect
                        value={profileDraft.cargo}
                        onChange={(v) => setProfileDraft(prev => ({ ...prev, cargo: v }))}
                        options={roleSuggestions}
                      />
                    </label>
                  </div>
                  <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                    <label style={s.fieldGroup}>
                      <span style={s.fieldLabel}>Email</span>
                      <input style={s.input} type="email" name="email" value={profileDraft.email} onChange={handleProfileChange} />
                    </label>
                    <PhoneField
                      label="WhatsApp"
                      value={profileDraft.whatsapp}
                      onChange={(v) => setProfileDraft(prev => ({ ...prev, whatsapp: v }))}
                    />
                  </div>
                  <label style={s.fieldGroup}>
                    <span style={s.fieldLabel}>Estado</span>
                    <select style={s.input} name="estado" value={profileDraft.estado} onChange={handleProfileChange}>
                      <option>Activo</option>
                      <option>Inactivo</option>
                    </select>
                  </label>

                  <div style={s.fieldGroup}>
                    <span style={s.fieldLabel}>Color en Journal</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: profileDraft.color || '#6B1C2A',
                          boxShadow: '0 0 0 2px #fff, 0 0 0 3px rgba(0,0,0,0.08)',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {COLOR_PALETTE.map(c => {
                          const active = (profileDraft.color || '').toLowerCase() === c.toLowerCase();
                          return (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setProfileDraft(prev => ({ ...prev, color: c }))}
                              aria-label={`Color ${c}`}
                              style={bigTap ? { ...{
                                width: 26, height: 26, borderRadius: '50%',
                                background: c,
                                border: active ? '2px solid #2D2D2D' : '2px solid #fff',
                                cursor: 'pointer', padding: 0,
                                boxShadow: active ? '0 0 0 2px rgba(45,45,45,0.15)' : '0 1px 4px rgba(0,0,0,0.15)',
                              }, width: 44, height: 44 } : {
                                width: 26, height: 26, borderRadius: '50%',
                                background: c,
                                border: active ? '2px solid #2D2D2D' : '2px solid #fff',
                                cursor: 'pointer', padding: 0,
                                boxShadow: active ? '0 0 0 2px rgba(45,45,45,0.15)' : '0 1px 4px rgba(0,0,0,0.15)',
                              }}
                            />
                          );
                        })}
                      </div>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                        <input
                          type="color"
                          value={profileDraft.color || '#6B1C2A'}
                          onChange={(e) => setProfileDraft(prev => ({ ...prev, color: e.target.value }))}
                          style={bigTap ? { ...{ width: 28, height: 28, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', cursor: 'pointer' }, width: 44, height: 44 } : { width: 28, height: 28, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                        />
                        Personalizado
                      </label>
                    </div>
                  </div>

                  {canViewMemberSensitive(selectedMember) && (
                    <>
                      <div style={s.adminSection}>
                        <span style={s.adminSectionTag}>
                          {isAdmin ? 'Solo admins' : 'Datos privados'}
                        </span>
                      </div>
                      <div style={s.fieldGroup}>
                        <span style={s.fieldLabel}>Cuenta bancaria</span>
                        <BankAccountField
                          account={profileDraft.cuenta_bancaria}
                          cci={profileDraft.cuenta_cci}
                          onAccountChange={(v) => setProfileDraft(prev => ({ ...prev, cuenta_bancaria: v }))}
                          onCciChange={(v) => setProfileDraft(prev => ({ ...prev, cuenta_cci: v }))}
                        />
                      </div>
                      <div style={isMobile ? { ...s.fieldRow, ...s.fieldRowMobile } : s.fieldRow}>
                        <label style={s.fieldGroup}>
                          <span style={s.fieldLabel}>Contacto emergencia — nombre</span>
                          <input style={s.input} name="contacto_emergencia_nombre" value={profileDraft.contacto_emergencia_nombre} onChange={handleProfileChange} />
                        </label>
                        <PhoneField
                          label="Contacto emergencia — teléfono"
                          value={profileDraft.contacto_emergencia_telefono}
                          onChange={(v) => setProfileDraft(prev => ({ ...prev, contacto_emergencia_telefono: v }))}
                        />
                      </div>
                    </>
                  )}

                  <div style={isMobile ? { ...s.modalActions, ...sheet.footer, ...s.modalActionsMobile } : s.modalActions}>
                    <button type="button" style={isMobile ? { ...s.cancelBtn, ...s.actionBtnMobile } : s.cancelBtn} onClick={closeProfile}>Cancelar</button>
                    <button type="button" style={isMobile ? { ...s.primaryBtn, ...s.actionBtnMobile } : s.primaryBtn} onClick={saveProfile} disabled={profileSaving}>
                      {profileSaving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                  </div>
                </div>
              )}

              {(() => {
                const myProjects = projectsForMember(selectedMember);
                return (
                  <>
                    <h4 style={s.sectionHead}>
                      Proyectos asignados
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--text-light)' }}>
                        ({myProjects.length})
                      </span>
                    </h4>
                    {myProjects.length === 0 ? (
                      <p style={{ fontSize: 13, color: 'var(--text-light)', padding: '8px 0 0' }}>
                        Esta persona no tiene proyectos asignados.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
                        {myProjects.map(p => {
                          const canNavigate = !!onNavigate;
                          return (
                            <div
                              key={p.id}
                              onClick={canNavigate ? () => {
                                onNavigate('gantt', { projectId: p.id, ganttType: 'obra' });
                                closeProfile();
                              } : undefined}
                              onMouseEnter={canNavigate ? e => {
                                e.currentTarget.style.borderColor = 'rgba(107,28,42,0.35)';
                                e.currentTarget.style.background = '#fff';
                                const arrow = e.currentTarget.querySelector('[data-row-arrow]');
                                if (arrow) arrow.style.opacity = '1';
                              } : undefined}
                              onMouseLeave={canNavigate ? e => {
                                e.currentTarget.style.borderColor = 'var(--border)';
                                e.currentTarget.style.background = '#FDFBF8';
                                const arrow = e.currentTarget.querySelector('[data-row-arrow]');
                                if (arrow) arrow.style.opacity = '0';
                              } : undefined}
                              style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                gap: 12, padding: '10px 14px', background: '#FDFBF8',
                                border: '1px solid var(--border)', borderRadius: 8,
                                cursor: canNavigate ? 'pointer' : 'default',
                                transition: 'border-color 0.15s, background 0.15s',
                              }}
                              role={canNavigate ? 'button' : undefined}
                              tabIndex={canNavigate ? 0 : undefined}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={isMobile ? { ...{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }, fontSize: 15 } : { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {p.nombre}
                                </div>
                                {p.estado && (
                                  <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>
                                    {p.estado}
                                  </div>
                                )}
                              </div>
                              {canNavigate && (
                                <span data-row-arrow style={{ color: 'var(--wine)', fontSize: 16, opacity: 0, transition: 'opacity 0.15s' }} aria-hidden="true">→</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div style={isMobile ? { ...s.overlay, ...dialog.overlay } : isTablet ? { ...s.overlay, ...s.overlayTablet } : s.overlay} onClick={closeConfirmDelete}>
          <div style={isMobile ? { ...s.modal, maxWidth: 440, ...dialog.panel } : { ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, paddingTop: 12, borderBottom: 'none' } : s.modalHeader}>
              <h3 style={s.modalTitle}>{confirmDelete.title}</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeConfirmDelete} disabled={deletingConfirm}>✕</button>
            </div>
            <div style={isMobile ? { padding: '4px 16px 16px' } : { padding: '14px 28px 24px' }}>
              <p style={isMobile ? { ...{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 20px' }, fontSize: 15 } : { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 20px' }}>
                {confirmDelete.message}
              </p>
              <div style={isMobile ? { ...s.modalActions, flexWrap: 'wrap' } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...s.actionBtnMobile } : s.cancelBtn} onClick={closeConfirmDelete} disabled={deletingConfirm}>
                  Cancelar
                </button>
                <button type="button" style={isMobile ? { ...s.dangerBtn, ...s.actionBtnMobile } : s.dangerBtn} onClick={runConfirmDelete} disabled={deletingConfirm}>
                  {deletingConfirm ? 'Eliminando…' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={isMobile ? { position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color:'#fff', padding:'10px 24px', borderRadius:10, fontSize:13, fontWeight:600,
          fontFamily:"'DM Sans',sans-serif", zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)', ...toastMobile } : { position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color:'#fff', padding:'10px 24px', borderRadius:10, fontSize:13, fontWeight:600,
          fontFamily:"'DM Sans',sans-serif", zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)' }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

// A dropdown backed by the roles table. Picking "Otro (escribir)" flips
// the same field into a free-text input so the user can enter a role
// that isn't in the catalog yet. Used for both Cargo (equipo) and
// Especialidad (proveedores).
function RoleSelect({ value, onChange, options, placeholder }) {
  const inCatalog = !value || options.includes(value);
  const [custom, setCustom] = useState(!inCatalog);

  // If an external update changes the value into/out of the catalog,
  // keep the mode in sync.
  useEffect(() => {
    if (value && !options.includes(value)) setCustom(true);
  }, [value, options]);

  if (custom) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          style={{ ...roleSelectStyles.input, flex: 1 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'Escribe un cargo'}
          autoFocus={!value}
        />
        <button
          type="button"
          onClick={() => { setCustom(false); onChange(''); }}
          style={roleSelectStyles.resetBtn}
          title="Volver a la lista de cargos"
          aria-label="Volver a la lista"
        >
          ↺
        </button>
      </div>
    );
  }

  return (
    <select
      style={roleSelectStyles.input}
      value={value || ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '__custom__') { setCustom(true); onChange(''); return; }
        onChange(v);
      }}
    >
      <option value="">{placeholder || '— Selecciona un cargo —'}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
      <option value="__custom__">Otro (escribir)</option>
    </select>
  );
}

const roleSelectStyles = {
  input: { padding:'8px 11px', borderRadius:7, border:'1px solid var(--border)', fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)', background:'#FDFBF8', outline:'none', width:'100%', boxSizing:'border-box' },
  resetBtn: { width:34, flexShrink:0, borderRadius:7, border:'1px solid var(--border)', background:'#fff', color:'var(--wine)', cursor:'pointer', fontSize:14, fontFamily:"'DM Sans',sans-serif" },
};

function Avatar({ member, size = 48, fontSize }) {
  const color = member.color || '#6B1C2A';
  const hasPhoto = !!member.foto_url;
  if (hasPhoto) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: `#fff url(${member.foto_url}) center/cover no-repeat`,
        boxShadow: `0 0 0 2px ${color}22`,
        flexShrink: 0,
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
      fontSize: fontSize ?? Math.max(11, Math.round(size * 0.34)),
      letterSpacing: '0.02em', flexShrink: 0,
    }}>
      {getInitials(member.nombre)}
    </div>
  );
}

function ProfileField({ label, value, mono }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:0 }}>
      <span style={{ fontSize:10, fontWeight:700, color:'var(--text-light)', textTransform:'uppercase', letterSpacing:'0.08em', display:'inline-flex', alignItems:'center', gap:6 }}>
        {label}
      </span>
      <span style={isMobile ? {
        fontSize:15, color: value ? 'var(--text-primary)' : 'var(--text-light)',
        wordBreak:'break-word',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
      } : {
        fontSize:13, color: value ? 'var(--text-primary)' : 'var(--text-light)',
        wordBreak:'break-word',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
      }}>
        {value || '—'}
      </span>
    </div>
  );
}

const s = {
  container: { padding:'32px 36px', maxWidth:1400 },
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title: { fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:600, color:'var(--wine)', margin:0 },
  subtitle: { fontSize:13, color:'var(--text-secondary)', marginTop:4 },
  primaryBtn: { padding:'9px 20px', borderRadius:8, border:'none', background:'#6B1C2A', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  secondaryBtn: { padding:'7px 14px', borderRadius:7, border:'1px solid var(--border)', background:'#fff', color:'var(--wine)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  loadingMsg: { background:'#fff', borderRadius:14, border:'1px solid var(--border)', padding:'40px 24px', textAlign:'center', color:'var(--text-light)', fontSize:14 },

  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:16 },
  card: { background:'#fff', borderRadius:14, border:'1px solid var(--border)', padding:'20px 22px', cursor:'pointer', transition:'transform 0.2s, box-shadow 0.2s' },

  memberName: { fontSize:15, fontWeight:600, color:'var(--text-primary)', margin:0, lineHeight:1.3 },
  memberRole: { fontSize:12, color:'var(--text-secondary)', margin:'3px 0 0' },
  estadoBadge: { fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:10, whiteSpace:'nowrap', textTransform:'uppercase', letterSpacing:'0.05em' },

  metaRow: { display:'flex', flexDirection:'column', gap:6, marginTop:14, paddingTop:14, borderTop:'1px solid #f3efe9' },
  metaItem: { display:'flex', flexDirection:'column', gap:2 },
  metaLabel: { fontSize:9, fontWeight:700, color:'var(--text-light)', textTransform:'uppercase', letterSpacing:'0.08em' },
  metaValue: { fontSize:12, color:'var(--text-primary)', wordBreak:'break-all' },

  cardFooter: { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:14, paddingTop:12, borderTop:'1px solid #f3efe9' },
  taskCount: { display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, color:'var(--wine)' },

  cameraOverlay: {
    position:'absolute', bottom:-2, right:-2,
    width:24, height:24, borderRadius:'50%',
    background:'#6B1C2A', color:'#fff', border:'2px solid #fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    cursor:'pointer', padding:0,
    boxShadow:'0 2px 4px rgba(0,0,0,0.15)',
    fontSize:11,
  },
  deletePhotoOverlay: {
    position:'absolute', top:-4, right:-4,
    width:20, height:20, borderRadius:'50%',
    background:'#B83B3B', color:'#fff', border:'2px solid #fff',
    display:'flex', alignItems:'center', justifyContent:'center',
    cursor:'pointer', padding:0,
    boxShadow:'0 2px 4px rgba(0,0,0,0.15)',
    fontSize:9, fontWeight:700, lineHeight:1,
  },

  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(2px)' },
  modal: { background:'#fff', borderRadius:16, width:'100%', maxWidth:520, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'22px 28px 0', gap:16 },
  profileHeader: { display:'flex', alignItems:'center', padding:'22px 28px 8px', gap:16 },
  photoActions: { display:'flex', gap:8, marginTop:8, flexWrap:'wrap' },
  photoBtn: {
    padding:'5px 10px', borderRadius:6, border:'1px solid var(--border)',
    background:'#fff', color:'var(--wine)', fontSize:11, fontWeight:600,
    cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
  },
  photoBtnDanger: {
    padding:'5px 10px', borderRadius:6, border:'1px solid rgba(184,59,59,0.3)',
    background:'rgba(184,59,59,0.06)', color:'#B83B3B', fontSize:11, fontWeight:600,
    cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
  },
  modalTitle: { fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:600, color:'var(--wine)', margin:0 },
  modalSubtitle: { fontSize:12, color:'var(--text-secondary)', margin:'2px 0 0' },
  closeBtn: { width:30, height:30, borderRadius:8, border:'1px solid var(--border)', background:'transparent', cursor:'pointer', fontSize:13, color:'var(--text-secondary)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },

  profileGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:14, marginBottom:20, paddingBottom:14, borderBottom:'1px solid #f3efe9' },
  adminTag: {
    display:'inline-block', padding:'1px 6px', borderRadius:10,
    background:'rgba(107,28,42,0.08)', color:'#6B1C2A', fontSize:9, fontWeight:700,
    letterSpacing:'0.08em', textTransform:'uppercase', marginLeft:6,
  },
  sectionHead: { fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:'var(--wine)', margin:'6px 0 10px' },
  sectionRow: { display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:16, margin:'8px 0 16px', flexWrap:'wrap' },
  sectionRowTitle: { fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:600, color:'var(--wine)', margin:0 },
  sectionRowSub: { fontSize:12, color:'var(--text-light)', margin:'2px 0 0' },
  avatarMark: {
    width:48, height:48, borderRadius:'50%',
    background:'#3E2118', color:'#C9A96E',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontWeight:700, fontFamily:"'DM Sans',sans-serif", fontSize:14,
    letterSpacing:'0.02em', flexShrink:0,
  },
  linkBtn: { border:'none', background:'transparent', color:'var(--wine)', fontSize:12, fontWeight:600, cursor:'pointer', padding:0, fontFamily:"'DM Sans',sans-serif" },

  editForm: { display:'flex', flexDirection:'column', gap:12, marginTop:4 },
  adminSection: { display:'flex', alignItems:'center', gap:10, margin:'6px 0 2px', paddingTop:10, borderTop:'1px solid #f3efe9' },
  adminSectionTag: { fontSize:10, fontWeight:700, color:'#6B1C2A', textTransform:'uppercase', letterSpacing:'0.1em' },

  form: { padding:'18px 28px 26px', display:'flex', flexDirection:'column', gap:14 },
  fieldRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  fieldGroup: { display:'flex', flexDirection:'column', gap:5 },
  fieldLabel: { fontSize:11, fontWeight:600, color:'var(--text-secondary)', letterSpacing:'0.02em' },
  input: { padding:'8px 11px', borderRadius:7, border:'1px solid var(--border)', fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)', background:'#FDFBF8', outline:'none', width:'100%', boxSizing:'border-box' },
  modalActions: { display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 },
  cancelBtn: { padding:'8px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", color:'var(--text-secondary)' },
  dangerBtn: { padding:'8px 18px', borderRadius:7, border:'none', background:'#B83B3B', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },
  trashBtn: {
    width:28, height:28, borderRadius:7,
    border:'1px solid rgba(184,59,59,0.25)',
    background:'rgba(184,59,59,0.08)', color:'#B83B3B',
    cursor:'pointer', padding:0,
    display:'flex', alignItems:'center', justifyContent:'center',
    flexShrink:0,
  },

  provFilterBar: {
    display:'flex', flexWrap:'wrap', alignItems:'center', gap:10,
    margin:'0 0 16px',
  },
  provSearchWrap: {
    position:'relative',
    display:'flex', alignItems:'center',
    background:'#fff', border:'1px solid var(--border)', borderRadius:10,
    padding:'0 10px',
    maxWidth:360,
  },
  provSearchIcon: {
    color:'var(--text-light)',
    display:'flex', alignItems:'center', justifyContent:'center',
    flexShrink:0,
  },
  provSearchInput: {
    flex:1, minWidth:0,
    border:'none', outline:'none',
    padding:'9px 8px',
    fontSize:13, fontFamily:"'DM Sans',sans-serif",
    color:'var(--text-primary)', background:'transparent',
  },
  provSearchClear: {
    width:22, height:22, borderRadius:'50%',
    border:'none', background:'rgba(0,0,0,0.06)',
    color:'var(--text-secondary)',
    cursor:'pointer', fontSize:11, lineHeight:1,
    display:'flex', alignItems:'center', justifyContent:'center',
    padding:0, flexShrink:0,
  },
  provEspWrap: {
    position:'relative',
  },
  provEspBtn: {
    display:'inline-flex', alignItems:'center', gap:8,
    padding:'9px 14px', borderRadius:10,
    border:'1px solid var(--border)', background:'#fff',
    color:'var(--text-primary)',
    fontSize:13, fontWeight:600, cursor:'pointer',
    fontFamily:"'DM Sans',sans-serif",
    whiteSpace:'nowrap',
  },
  provEspBtnActive: {
    borderColor:'#6B1C2A',
    color:'#6B1C2A',
    background:'rgba(107,28,42,0.06)',
  },
  provEspCaret: {
    fontSize:10, color:'var(--text-light)', lineHeight:1,
  },
  provEspDropdown: {
    position:'absolute',
    top:'calc(100% + 4px)', left:0,
    minWidth:260, maxWidth:320,
    background:'#fff',
    border:'1px solid var(--border)', borderRadius:10,
    boxShadow:'0 12px 28px rgba(0,0,0,0.12)',
    zIndex:1500,
    overflow:'hidden',
  },
  provEspList: {
    maxHeight:280, overflowY:'auto',
    padding:6,
    display:'flex', flexDirection:'column',
  },
  provEspItem: {
    display:'flex', alignItems:'center', gap:10,
    width:'100%', padding:'8px 10px',
    border:'none', background:'transparent',
    textAlign:'left', cursor:'pointer',
    borderRadius:7,
    fontSize:13, fontFamily:"'DM Sans',sans-serif",
    color:'var(--text-primary)',
  },
  provEspItemLabel: {
    flex:1, minWidth:0,
    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
  },
  provEspCheckbox: {
    width:18, height:18, borderRadius:4,
    border:'1.5px solid var(--border)', background:'#FDFBF8',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:11, fontWeight:700, color:'#fff',
    flexShrink:0,
  },
  provEspCheckboxActive: {
    background:'#6B1C2A', borderColor:'#6B1C2A',
  },
  provEspClearBtn: {
    width:'100%', padding:'10px 14px',
    border:'none', borderTop:'1px solid var(--border)',
    background:'#FDFBF8',
    color:'#6B1C2A', fontSize:12, fontWeight:600, cursor:'pointer',
    fontFamily:"'DM Sans',sans-serif",
    textAlign:'center',
  },

  // Mobile/tablet-only additions (merged on top of the keys above only when
  // useBreakpoint() reports a compact viewport).
  overlayTablet: { padding:16, boxSizing:'border-box' },
  modalHeaderMobile: {
    padding:'0 8px 10px 16px',
    paddingTop:'max(12px, env(safe-area-inset-top))',
    alignItems:'center',
    borderBottom:'1px solid #f3efe9',
  },
  modalTitleMobile: { flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  profileHeaderMobile: { padding:'16px 16px 8px', alignItems:'flex-start' },
  formMobile: { padding:'16px 16px 0' },
  fieldRowMobile: { gridTemplateColumns:'minmax(0, 1fr)' },
  modalActionsMobile: {
    margin:'4px -16px 0',
    padding:'12px 16px',
    paddingBottom:'max(12px, env(safe-area-inset-bottom))',
    borderTop:'1px solid #f3efe9',
  },
  actionBtnMobile: { flex:1, minHeight:44 },

  // Phase 4 — page layout on mobile / touch.
  sectionRowMobile: { flexDirection:'column', alignItems:'stretch', gap:12 },
  gridMobile: { gridTemplateColumns:'minmax(0, 1fr)', gap:12 },
  loadingMsgMobile: { fontSize:15 },
  memberRoleMobile: { fontSize:15 },
  metaLabelMobile: { fontSize:11 },
  metaValueMobile: { fontSize:15, wordBreak:'break-word', overflowWrap:'anywhere' },
  // 44px hit area; background-clip keeps the visible 24px / 20px circle.
  cameraOverlayTouch: {
    width:44, height:44, padding:10, bottom:-12, right:-12,
    backgroundClip:'content-box', border:'none', boxShadow:'none',
  },
  deletePhotoOverlayTouch: {
    width:44, height:44, padding:12, top:-16, left:-16, right:'auto',
    backgroundClip:'content-box', border:'none', boxShadow:'none', fontSize:11,
  },
  provFilterBarMobile: { flexDirection:'column', alignItems:'stretch' },
  provSearchWrapMobile: { maxWidth:'100%', minHeight:44 },
  provSearchClearTouch: {
    width:44, height:44, padding:11, marginRight:-10,
    backgroundClip:'content-box',
  },
  provEspItemMobile: { minHeight:44, fontSize:15 },
  photoBtnTouch: { minHeight:44, padding:'0 14px', fontSize:13 },
};
